/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { X } from "lucide-react";
import React from "react";
import type { ReactElement } from "react";
import { Checkbox } from "../../../shared/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../../shared/components/ui/select";
import { Slider } from "../../../shared/components/ui/slider";

export interface FilterState {
  yearRange: [number, number];
  country: string | null;
  genre: string | null;
  director: string | null;
  language: string | null;
  titleId: string | null; // For filtering to a single title from marker click
  watchlistOnly: boolean; // Filter to show only titles in user's watchlist
}

interface FilterPanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly filterState: FilterState;
  readonly onFilterChange: (updates: Partial<FilterState>) => void;
  readonly onClearFilters: () => void;
  readonly hasActiveFilters: boolean;
  readonly availableCountries: string[];
  readonly availableGenres: string[];
  readonly availableDirectors: string[];
  readonly availableLanguages: string[];
  readonly yearBounds: { min: number; max: number };
  readonly isAuthenticated: boolean;
  readonly watchlistCount: number;
  readonly watchlistLoading: boolean;
}

/**
 * Floating filter panel for GroupedBrowsePage
 * Positioned at top-left with backdrop blur styling
 */
export function FilterPanel({
  isOpen,
  onClose,
  filterState,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
  availableCountries,
  availableGenres,
  availableDirectors,
  availableLanguages,
  yearBounds,
  isAuthenticated,
  watchlistCount,
  watchlistLoading,
}: Readonly<FilterPanelProps>): ReactElement | null {
  // Local state for year range with debouncing
  const [localYearRange, setLocalYearRange] = React.useState<[number, number]>(filterState.yearRange);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Sync local state when filterState changes from external source (e.g., map click)
  React.useEffect(() => {
    setLocalYearRange(filterState.yearRange);
  }, [filterState.yearRange]);

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleYearRangeChange = (values: number[]) => {
    if (values.length === 2) {
      const newRange: [number, number] = [values[0], values[1]];
      setLocalYearRange(newRange); // Update local state immediately for smooth UI

      // Debounce the actual filter update
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        onFilterChange({ yearRange: newRange });
      }, 150); // 150ms debounce for snappy but not too rapid updates
    }
  };

  const handleCountryChange = (value: string) => {
    onFilterChange({ country: value === "all" ? null : value });
  };

  const handleGenreChange = (value: string) => {
    onFilterChange({ genre: value === "all" ? null : value });
  };

  const handleDirectorChange = (value: string) => {
    onFilterChange({ director: value === "all" ? null : value });
  };

  const handleLanguageChange = (value: string) => {
    onFilterChange({ language: value === "all" ? null : value });
  };

  // Animation state for smooth transitions
  const [shouldRender, setShouldRender] = React.useState(isOpen);
  const [isAnimating, setIsAnimating] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Delay to allow DOM to render before animation starts
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to finish before removing from DOM
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <>
      {/* Filter Panel */}
      <div
        className={`fixed top-20 left-6 z-50 w-[300px] bg-black/50 backdrop-blur-md rounded-lg border border-lumi-accent/50 shadow-2xl pointer-events-auto transition-all duration-300 ease-out ${
          isAnimating ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between py-4 px-6 border-b border-neutral-800">
          <h3 className="text-sm font-medium text-white">Filters</h3>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-sm text-neutral-400 hover:text-[var] transition-colors"
            >
              Clear Filters
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-red-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter Content */}
        <div className="p-6 space-y-6">
          {/* Watchlist Filter - Only show when authenticated */}
          {isAuthenticated && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <label htmlFor="watchlist-checkbox" className="text-xs font-medium text-neutral-400 cursor-pointer">
                    Watchlist
                  </label>
                  {filterState.watchlistOnly && watchlistCount > 0 && (
                    <span className="text-xs text-lumi-accent">
                      - {watchlistCount} title{watchlistCount === 1 ? "" : "s"}
                    </span>
                  )}
                  {watchlistCount === 0 && !watchlistLoading && (
                    <span className="text-xs text-neutral-500">- 0 Titles</span>
                  )}
                </div>
                <Checkbox
                  id="watchlist-checkbox"
                  checked={filterState.watchlistOnly}
                  onCheckedChange={(checked) => {
                    onFilterChange({ watchlistOnly: checked === true });
                  }}
                  disabled={watchlistLoading || watchlistCount === 0}
                />
              </div>
            </div>
          )}

          {/* Country Filter */}
          <div className="space-y-2">
            <div
              className={
                filterState.country ? "text-xs font-medium text-lumi-accent" : "text-xs font-medium text-neutral-400"
              }
            >
              Country
            </div>
            <Select value={filterState.country || "all"} onValueChange={handleCountryChange}>
              <SelectTrigger className="w-full">{filterState.country || "All Countries"}</SelectTrigger>
              <SelectContent className="w-[250px] mt-1 bg-neutral-900/40">
                <SelectItem value="all">All Countries</SelectItem>
                {availableCountries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Genre Filter */}
          <div className="space-y-2">
            <div
              className={
                filterState.genre ? "text-xs font-medium text-lumi-accent" : "text-xs font-medium text-neutral-400"
              }
            >
              Genre
            </div>
            <Select value={filterState.genre || "all"} onValueChange={handleGenreChange}>
              <SelectTrigger className="w-full">{filterState.genre || "All Genres"}</SelectTrigger>
              <SelectContent className="w-[250px] mt-1 bg-neutral-900/40">
                <SelectItem value="all">All Genres</SelectItem>
                {availableGenres.map((genre) => (
                  <SelectItem key={genre} value={genre}>
                    {genre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Director Filter */}
          <div className="space-y-2">
            <div
              className={
                filterState.director ? "text-xs font-medium text-lumi-accent" : "text-xs font-medium text-neutral-400"
              }
            >
              Director
            </div>
            <Select value={filterState.director || "all"} onValueChange={handleDirectorChange}>
              <SelectTrigger className="w-full">{filterState.director || "All Directors"}</SelectTrigger>
              <SelectContent className="mt-1 bg-neutral-900/40">
                <SelectItem value="all">All Directors</SelectItem>
                {availableDirectors.map((director) => (
                  <SelectItem key={director} value={director}>
                    {director}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Language Filter */}
          <div className="space-y-2">
            <div
              className={
                filterState.language ? "text-xs font-medium text-lumi-accent" : "text-xs font-medium text-neutral-400"
              }
            >
              Language
            </div>
            <Select value={filterState.language || "all"} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-full">{filterState.language || "All Languages"}</SelectTrigger>
              <SelectContent className="w-[250px] mt-1 bg-neutral-900/40">
                <SelectItem value="all">All Languages</SelectItem>
                {availableLanguages.map((language) => (
                  <SelectItem key={language} value={language}>
                    {language}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year Range Filter */}
          <div className="space-y-4">
            <div
              className={
                filterState.yearRange[0] !== yearBounds.min || filterState.yearRange[1] !== yearBounds.max
                  ? "text-xs font-medium text-lumi-accent"
                  : "text-xs font-medium text-neutral-400"
              }
            >
              Year Range
            </div>
            <Slider
              min={yearBounds.min}
              max={yearBounds.max}
              step={1}
              value={localYearRange}
              onValueChange={handleYearRangeChange}
              className="w-full"
            />
            <div className="flex justify-between text-sm">
              <span
                className={
                  localYearRange[0] !== yearBounds.min || localYearRange[1] !== yearBounds.max
                    ? "text-xs text-lumi-accent"
                    : "text-xs text-neutral-400"
                }
              >
                {localYearRange[0]}
              </span>
              <span
                className={
                  localYearRange[0] !== yearBounds.min || localYearRange[1] !== yearBounds.max
                    ? "text-xs text-lumi-accent"
                    : "text-xs text-neutral-400"
                }
              >
                {localYearRange[1]}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
